import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import CONFIG from '../../config';
import NavBarEstudianteTutor from './NavBarEstudianteTutor';

ChartJS.register(ArcElement, Tooltip, Legend);

const URL_API = `${CONFIG.API_URL}/operarAsistencia.php`;

function AsistenciaDetalleEstudiante({ acceder, configuracion, studentIdProp, studentDataProp }) {
    const navigate = useNavigate();
    const anioActual = new Date().getFullYear();
    const [anioLectivo, setAnioLectivo] = useState(anioActual);
    const [asistencias, setAsistencias] = useState([]);
    const [cargando, setCargando] = useState(true);

    const llamoTutor = localStorage.getItem('kte') || null;
    const userRol = localStorage.getItem('loggeduserRolId');
    const esTutor = Boolean(llamoTutor || userRol == 8 || userRol == "8");

    const datoEstudiante = studentDataProp || (() => {
        if (llamoTutor && llamoTutor !== "null") {
            try {
                const [, estudianteStr] = llamoTutor.split("&");
                return JSON.parse(estudianteStr);
            } catch (e) {
                return null;
            }
        }
        return null;
    })();

    const effectiveUserId = studentIdProp || (datoEstudiante && (datoEstudiante.estudiante_id || datoEstudiante.id)) || parseInt(localStorage.getItem('loggedUserId'), 10) || null;

    useEffect(() => {
        if (!effectiveUserId) return;
        setCargando(true);
        axios.get(`${URL_API}?id_estudiante=${effectiveUserId}&anioLectivo=${anioLectivo}`)
            .then((res) => {
                if (res.data && res.data.resultado) {
                    setAsistencias(res.data.asistencia || []);
                } else {
                    setAsistencias([]);
                }
            })
            .catch((err) => {
                console.error("Error al obtener asistencia:", err);
                setAsistencias([]);
            })
            .finally(() => {
                setCargando(false);
            });
    }, [effectiveUserId, anioLectivo]);

    // Función auxiliar para formatear fecha a "Día, DD de Mes de YYYY"
    const formatFechaLarga = (fechaStr) => {
        if (!fechaStr) return '';
        const partes = fechaStr.split('-');
        if (partes.length === 3) {
            const date = new Date(parseInt(partes[0]), parseInt(partes[1]) - 1, parseInt(partes[2]));
            const opciones = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
            const formateada = date.toLocaleDateString('es-ES', opciones);
            return formateada.charAt(0).toUpperCase() + formateada.slice(1);
        }
        return fechaStr;
    };

    const [filtroJornada, setFiltroJornada] = useState('todos'); // 'todos' | 'turno' | 'contraturno'

    // Separación de asistencias Diarias (Turno 0 o Contraturno -1) vs Materias (>0)
    const asistenciasDiariasAll = asistencias.filter(item => item.curso_grupo == 0 || item.curso_grupo === '0' || item.curso_grupo == -1 || item.curso_grupo === '-1');
    const asistenciasMaterias = asistencias.filter(item => item.curso_grupo > 0);

    const asistenciasDiarias = asistenciasDiariasAll.filter(item => {
        if (filtroJornada === 'turno') return item.curso_grupo == 0 || item.curso_grupo === '0';
        if (filtroJornada === 'contraturno') return item.curso_grupo == -1 || item.curso_grupo === '-1';
        return true;
    });

    // Conteo diario general
    const conteoDiario = asistenciasDiarias.reduce(
        (acc, item) => {
            if (item.asistencia === "Presente") acc.presentes++;
            else if (item.asistencia === "Ausente") acc.ausentes++;
            else if (item.asistencia === "Tarde") acc.tarde++;
            else if (item.asistencia === "Retiro") acc.retiro++;
            return acc;
        },
        { presentes: 0, ausentes: 0, tarde: 0, retiro: 0 }
    );

    const inasistenciasDiarias = asistenciasDiarias.filter(item => item.asistencia === 'Ausente');
    const tardanzasDiarias = asistenciasDiarias.filter(item => item.asistencia === 'Tarde');
    const retirosDiarios = asistenciasDiarias.filter(item => item.asistencia === 'Retiro');

    // Datos del gráfico general
    const dataGraficoGeneral = {
        labels: ["Presente", "Ausente", "Tarde", "Retiro"],
        datasets: [
            {
                data: [conteoDiario.presentes, conteoDiario.ausentes, conteoDiario.tarde, conteoDiario.retiro],
                backgroundColor: ["#198754", "#dc3545", "#ffc107", "#fd7e14"],
                borderColor: ["#ffffff", "#ffffff", "#ffffff", "#ffffff"],
                borderWidth: 2,
            },
        ],
    };

    const optionsGrafico = {
        plugins: {
            legend: {
                position: "bottom",
                labels: {
                    boxWidth: 12,
                    padding: 10,
                    font: { size: 12 }
                },
            },
        },
        maintainAspectRatio: false,
    };

    const optionsGraficoMateria = {
        plugins: {
            legend: {
                position: "bottom",
                labels: {
                    boxWidth: 10,
                    padding: 8,
                    font: { size: 11 }
                },
            },
        },
        maintainAspectRatio: false,
    };

    // Agrupación de asistencia por Materia / Espacio
    const materiasMap = {};
    asistenciasMaterias.forEach(item => {
        const nombreMateria = item.nombre_espacio || item.curso_denominacion || `Materia #${item.curso_grupo}`;
        if (!materiasMap[nombreMateria]) {
            materiasMap[nombreMateria] = {
                nombre: nombreMateria,
                presentes: 0,
                ausentes: 0,
                tarde: 0,
                retiro: 0,
                inasistencias: [],
                tardanzas: [],
                retiros: []
            };
        }
        if (item.asistencia === "Presente") materiasMap[nombreMateria].presentes++;
        else if (item.asistencia === "Ausente") {
            materiasMap[nombreMateria].ausentes++;
            materiasMap[nombreMateria].inasistencias.push(item);
        } else if (item.asistencia === "Tarde") {
            materiasMap[nombreMateria].tarde++;
            materiasMap[nombreMateria].tardanzas.push(item);
        } else if (item.asistencia === "Retiro") {
            materiasMap[nombreMateria].retiro++;
            materiasMap[nombreMateria].retiros.push(item);
        }
    });

    const materiasList = Object.values(materiasMap);

    // Cálculo de diferencias / discrepancias entre Asistencia Diaria y Asistencia por Materia
    const discrepancias = [];
    const fechasMap = {};

    asistencias.forEach(item => {
        if (!fechasMap[item.fecha]) {
            fechasMap[item.fecha] = { diaria: null, materias: [] };
        }
        if (item.curso_grupo == 0 || item.curso_grupo === '0') {
            fechasMap[item.fecha].diaria = item;
        } else {
            fechasMap[item.fecha].materias.push(item);
        }
    });

    Object.keys(fechasMap).forEach(fecha => {
        const registroDiaria = fechasMap[fecha].diaria;
        const registrosMaterias = fechasMap[fecha].materias;

        if (registroDiaria && registrosMaterias.length > 0) {
            const estadoDiario = registroDiaria.asistencia;
            registrosMaterias.forEach(mat => {
                const estadoMateria = mat.asistencia;
                const nombreMateria = mat.nombre_espacio || mat.curso_denominacion || `Materia #${mat.curso_grupo}`;

                const esPresenteDiaria = estadoDiario === 'Presente' || estadoDiario === 'Tarde' || estadoDiario === 'Retiro';
                const esPresenteMateria = estadoMateria === 'Presente' || estadoMateria === 'Tarde' || estadoMateria === 'Retiro';

                // Solo hay discrepancia real si uno indica presencia (Presente/Tarde/Retiro) y la materia indica Ausente (o viceversa)
                if (esPresenteDiaria !== esPresenteMateria) {
                    let detalle = '';
                    let tipoBadge = 'warning';

                    if (esPresenteDiaria && estadoMateria === 'Ausente') {
                        detalle = `Estuvo ${estadoDiario.toUpperCase()} en la asistencia general diaria, pero figuró AUSENTE en ${nombreMateria}.`;
                        tipoBadge = 'danger';
                    } else if (estadoDiario === 'Ausente' && esPresenteMateria) {
                        detalle = `Estuvo AUSENTE en la asistencia general diaria, pero figuró ${estadoMateria.toUpperCase()} en ${nombreMateria}.`;
                        tipoBadge = 'warning';
                    }

                    discrepancias.push({
                        fecha,
                        estadoDiario,
                        estadoMateria,
                        nombreMateria,
                        detalle,
                        tipoBadge
                    });
                }
            });
        }
    });

    return (
        <div className="container-principal">
            <div className="container-fluid py-3 px-md-4">
            {esTutor && datoEstudiante && (
                <NavBarEstudianteTutor estudiante={datoEstudiante} configuracion={configuracion} />
            )}

            {/* Encabezado y Selector de Año */}
            <div className="card shadow-sm border-0 mb-4 bg-white rounded-3">
                <div className="card-body d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
                    <div>
                        <div className="d-flex align-items-center gap-2">
                            <Link to="/Principal" className="btn btn-sm btn-outline-secondary rounded-circle me-2" title="Volver a Principal">
                                <i className="fa-solid fa-arrow-left"></i>
                            </Link>
                            <h4 className="fw-bold mb-0 text-dark">
                                <i className="fa-solid fa-chart-pie text-primary me-2"></i>
                                Estadística y Detalle de Asistencia
                            </h4>
                        </div>
                        <p className="text-muted small mb-0 mt-1">
                            Monitoreo exhaustivo de asistencia general diaria y de espacios curriculares.
                        </p>
                    </div>

                    <div className="d-flex align-items-center gap-2">
                        <label htmlFor="selectAnio" className="form-label small fw-bold mb-0 text-nowrap">Ciclo Lectivo:</label>
                        <select
                            id="selectAnio"
                            className="form-select form-select-sm w-auto shadow-sm"
                            value={anioLectivo}
                            onChange={(e) => setAnioLectivo(Number(e.target.value))}
                        >
                            <option value={anioActual}>{anioActual}</option>
                            <option value={anioActual - 1}>{anioActual - 1}</option>
                            <option value={anioActual - 2}>{anioActual - 2}</option>
                        </select>
                    </div>
                </div>
            </div>

            {cargando ? (
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Cargando datos...</span>
                    </div>
                    <p className="text-muted mt-2 small">Cargando estadística de asistencia...</p>
                </div>
            ) : (
                <>
                    {/* SECCIÓN 1: ASISTENCIA GENERAL DIARIA */}
                    <div className="row mb-4">
                        <div className="col-12">
                            <div className="card shadow-sm border-0 rounded-3">
                                <div className="card-header bg-primary bg-gradient text-white fw-bold d-flex flex-column flex-sm-row align-items-sm-center justify-content-between gap-2 py-2">
                                    <span className="d-flex align-items-center">
                                        <i className="fa-solid fa-school me-2"></i>
                                        Asistencia General Diaria
                                    </span>

                                    {/* Selector de filtro por Turno / Contraturno */}
                                    <div className="d-flex align-items-center gap-2">
                                        <div className="btn-group btn-group-sm" role="group">
                                            <button
                                                type="button"
                                                className={`btn btn-sm ${filtroJornada === 'todos' ? 'btn-light text-dark fw-bold' : 'btn-outline-light'}`}
                                                onClick={() => setFiltroJornada('todos')}
                                            >
                                                Todos
                                            </button>
                                            <button
                                                type="button"
                                                className={`btn btn-sm ${filtroJornada === 'turno' ? 'btn-light text-dark fw-bold' : 'btn-outline-light'}`}
                                                onClick={() => setFiltroJornada('turno')}
                                            >
                                                Turno
                                            </button>
                                            <button
                                                type="button"
                                                className={`btn btn-sm ${filtroJornada === 'contraturno' ? 'btn-light text-dark fw-bold' : 'btn-outline-light'}`}
                                                onClick={() => setFiltroJornada('contraturno')}
                                            >
                                                Contraturno
                                            </button>
                                        </div>
                                        <span className="badge bg-light text-primary">
                                            Total: {conteoDiario.presentes + conteoDiario.ausentes + conteoDiario.tarde + conteoDiario.retiro}
                                        </span>
                                    </div>
                                </div>
                                <div className="card-body">
                                    <div className="row align-items-center">
                                        {/* Gráfico Doughnut */}
                                        <div className="col-12 col-md-5 col-lg-4 mb-3 mb-md-0">
                                            <div style={{ height: "12rem" }}>
                                                {conteoDiario.presentes + conteoDiario.ausentes + conteoDiario.tarde + conteoDiario.retiro > 0 ? (
                                                    <Doughnut data={dataGraficoGeneral} options={optionsGrafico} />
                                                ) : (
                                                    <div className="text-center text-muted py-4 small">
                                                        Sin registros de asistencia diaria
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Tarjetas de Resumen */}
                                        <div className="col-12 col-md-7 col-lg-8">
                                            {(() => {
                                                const totalPresentes = conteoDiario.presentes + conteoDiario.tarde;
                                                const totalFaltas = conteoDiario.ausentes + (conteoDiario.tarde * 0.25) + (conteoDiario.retiro * 0.25);
                                                const faltasFormateadas = totalFaltas % 1 === 0 ? totalFaltas : totalFaltas.toFixed(2);

                                                return (
                                                    <div className="row g-2 align-items-stretch">
                                                        {/* Cuadro de Presentes con sub-cuadros para Tardes y Retiros */}
                                                        <div className="col-12 col-sm-5">
                                                            <div className="p-3 bg-success bg-opacity-10 border border-success border-opacity-25 rounded text-center h-100 d-flex flex-column justify-content-between">
                                                                <div>
                                                                    <div className="text-success fw-bold fs-3 mb-0">{totalPresentes}</div>
                                                                    <div className="text-dark small fw-bold">Presentes</div>
                                                                    <div className="text-muted" style={{ fontSize: '0.72rem' }}>(Presentes + Tardes)</div>
                                                                </div>

                                                                {/* Sub-cuadros pequeños dentro del cuadro Presentes */}
                                                                <div className="row g-1 mt-2">
                                                                    <div className="col-6">
                                                                        <div className="bg-warning bg-opacity-10 border border-warning border-opacity-50 rounded p-1 text-center">
                                                                            <div className="fw-bold text-warning-emphasis small">{conteoDiario.tarde}</div>
                                                                            <div className="text-dark" style={{ fontSize: '0.68rem' }}>Tardes (¼)</div>
                                                                        </div>
                                                                    </div>
                                                                    <div className="col-6">
                                                                        <div className="bg-warning bg-opacity-25 border border-warning rounded p-1 text-center">
                                                                            <div className="fw-bold text-dark small">{conteoDiario.retiro}</div>
                                                                            <div className="text-dark" style={{ fontSize: '0.68rem' }}>Retiros (¼)</div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* Cuadro de Ausentes */}
                                                        <div className="col-6 col-sm-3">
                                                            <div className="p-3 bg-danger bg-opacity-10 border border-danger border-opacity-25 rounded text-center h-100 d-flex flex-column justify-content-center">
                                                                <div className="text-danger fw-bold fs-2 mb-0">{conteoDiario.ausentes}</div>
                                                                <div className="text-dark small fw-bold">Ausentes</div>
                                                                <div className="text-muted" style={{ fontSize: '0.72rem' }}>(1 falta c/u)</div>
                                                            </div>
                                                        </div>

                                                        {/* Contador de Faltas */}
                                                        <div className="col-6 col-sm-4">
                                                            <div className="p-3 bg-info bg-opacity-10 border border-info border-opacity-50 rounded text-center h-100 d-flex flex-column justify-content-center">
                                                                <div className="text-primary-emphasis fw-bold fs-2 mb-0">{faltasFormateadas}</div>
                                                                <div className="text-dark small fw-bold">Contador de Faltas</div>
                                                                <div className="text-muted" style={{ fontSize: '0.7rem' }}>1 Aus. + ¼ Tarde + ¼ Retiro</div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })()}

                                            {/* Detalle Tabulado de Inasistencias, Tardanzas y Retiros */}
                                            <div className="row mt-4 g-2">
                                                <div className="col-12 col-md-4 mb-2">
                                                    <div className="card h-100 border">
                                                        <div className="card-header bg-light py-2 small fw-bold text-danger d-flex align-items-center">
                                                            <i className="fa-solid fa-user-xmark me-2"></i>
                                                            Inasistencias ({inasistenciasDiarias.length})
                                                        </div>
                                                        <div className="card-body p-2" style={{ maxHeight: "200px", overflowY: "auto" }}>
                                                            {inasistenciasDiarias.length > 0 ? (
                                                                <ul className="list-group list-group-flush small">
                                                                    {inasistenciasDiarias.map((item, idx) => (
                                                                        <li key={idx} className="list-group-item d-flex justify-content-between align-items-center px-2 py-1">
                                                                            <span>
                                                                                {formatFechaLarga(item.fecha)}
                                                                                {(item.curso_grupo == -1 || item.curso_grupo === '-1') && (
                                                                                    <span className="badge text-white ms-1" style={{ backgroundColor: '#6f42c1', fontSize: '0.65rem' }}>Contraturno</span>
                                                                                )}
                                                                            </span>
                                                                            <span className="badge bg-danger">Ausente</span>
                                                                        </li>
                                                                    ))}
                                                                </ul>
                                                            ) : (
                                                                <div className="text-muted small text-center py-3">
                                                                    No registra inasistencias.
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="col-12 col-md-4 mb-2">
                                                    <div className="card h-100 border">
                                                        <div className="card-header bg-light py-2 small fw-bold text-warning-emphasis d-flex align-items-center">
                                                            <i className="fa-solid fa-clock me-2"></i>
                                                            Llegadas Tardes ({tardanzasDiarias.length})
                                                        </div>
                                                        <div className="card-body p-2" style={{ maxHeight: "200px", overflowY: "auto" }}>
                                                            {tardanzasDiarias.length > 0 ? (
                                                                <ul className="list-group list-group-flush small">
                                                                    {tardanzasDiarias.map((item, idx) => (
                                                                        <li key={idx} className="list-group-item d-flex justify-content-between align-items-center px-2 py-1">
                                                                            <span>
                                                                                {formatFechaLarga(item.fecha)}
                                                                                {(item.curso_grupo == -1 || item.curso_grupo === '-1') && (
                                                                                    <span className="badge text-white ms-1" style={{ backgroundColor: '#6f42c1', fontSize: '0.65rem' }}>Contraturno</span>
                                                                                )}
                                                                            </span>
                                                                            <span className="badge bg-warning text-dark">Tarde</span>
                                                                        </li>
                                                                    ))}
                                                                </ul>
                                                            ) : (
                                                                <div className="text-muted small text-center py-3">
                                                                    No registra tardanzas.
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="col-12 col-md-4 mb-2">
                                                    <div className="card h-100 border">
                                                        <div className="card-header bg-light py-2 small fw-bold text-dark d-flex align-items-center">
                                                            <i className="fa-solid fa-person-walking-arrow-right me-2 text-warning"></i>
                                                            Retiros ({retirosDiarios.length})
                                                        </div>
                                                        <div className="card-body p-2" style={{ maxHeight: "200px", overflowY: "auto" }}>
                                                            {retirosDiarios.length > 0 ? (
                                                                <ul className="list-group list-group-flush small">
                                                                    {retirosDiarios.map((item, idx) => (
                                                                        <li key={idx} className="list-group-item d-flex justify-content-between align-items-center px-2 py-1">
                                                                            <span>
                                                                                {formatFechaLarga(item.fecha)}
                                                                                {(item.curso_grupo == -1 || item.curso_grupo === '-1') && (
                                                                                    <span className="badge text-white ms-1" style={{ backgroundColor: '#6f42c1', fontSize: '0.65rem' }}>Contraturno</span>
                                                                                )}
                                                                            </span>
                                                                            <span className="badge bg-warning text-dark border border-dark">Retiro</span>
                                                                        </li>
                                                                    ))}
                                                                </ul>
                                                            ) : (
                                                                <div className="text-muted small text-center py-3">
                                                                    No registra retiros.
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* SECCIÓN 2: ASISTENCIA POR MATERIA / ESPACIO CURRICULAR */}
                    {materiasList.length > 0 && (
                        <div className="row mb-4">
                            <div className="col-12">
                                <div className="card shadow-sm border-0 rounded-3">
                                    <div className="card-header bg-secondary bg-gradient text-white fw-bold d-flex align-items-center justify-content-between py-2">
                                        <span>
                                            <i className="fa-solid fa-book-bookmark me-2"></i>
                                            Asistencia por Espacio Curricular / Materia
                                        </span>
                                        <span className="badge bg-light text-dark">
                                            {materiasList.length} Espacios con Registro
                                        </span>
                                    </div>
                                    <div className="card-body">
                                        <div className="row g-4">
                                            {materiasList.map((mat, index) => {
                                                const totalMat = mat.presentes + mat.ausentes + mat.tarde + mat.retiro;
                                                const dataMat = {
                                                    labels: ["Presente", "Ausente", "Tarde", "Retiro"],
                                                    datasets: [
                                                        {
                                                            data: [mat.presentes, mat.ausentes, mat.tarde, mat.retiro],
                                                            backgroundColor: ["#75b798", "#f87171", "#fde047", "#ffb74d"],
                                                            borderColor: ["#ffffff", "#ffffff", "#ffffff", "#ffffff"],
                                                            borderWidth: 1,
                                                        },
                                                    ],
                                                };

                                                return (
                                                    <div className="col-12 col-md-6 col-xl-4" key={index}>
                                                        <div className="card h-100 border shadow-sm rounded-3">
                                                            <div className="card-header bg-light fw-bold text-truncate py-2 small">
                                                                <i className="fa-solid fa-graduation-cap me-1 text-primary"></i>
                                                                {mat.nombre}
                                                            </div>
                                                            <div className="card-body p-3">
                                                                {/* Gráfico de Materia */}
                                                                <div style={{ height: "9rem" }} className="mb-2">
                                                                    <Doughnut data={dataMat} options={optionsGraficoMateria} />
                                                                </div>

                                                                {/* Valores numéricos */}
                                                                <div className="d-flex justify-content-around small text-muted border-top border-bottom py-1 mb-2">
                                                                    <span><strong>Pres:</strong> {mat.presentes}</span>
                                                                    <span className="text-danger"><strong>Aus:</strong> {mat.ausentes}</span>
                                                                    <span className="text-warning-emphasis"><strong>Tar:</strong> {mat.tarde}</span>
                                                                    <span className="text-dark"><strong>Ret:</strong> {mat.retiro}</span>
                                                                    <span><strong>Total:</strong> {totalMat}</span>
                                                                </div>

                                                                {/* Listado de ausencias, tardanzas y retiros de esta materia */}
                                                                <div className="accordion accordion-flush" id={`acc-${index}`}>
                                                                    <div className="accordion-item border rounded">
                                                                        <h2 className="accordion-header" id={`heading-${index}`}>
                                                                            <button
                                                                                className="accordion-button collapsed py-1 px-2 small text-muted"
                                                                                type="button"
                                                                                data-bs-toggle="collapse"
                                                                                data-bs-target={`#collapse-${index}`}
                                                                                aria-expanded="false"
                                                                                aria-controls={`collapse-${index}`}
                                                                            >
                                                                                Detalle inasistencias ({mat.inasistencias.length}), tardanzas ({mat.tardanzas.length}) y retiros ({mat.retiros.length})
                                                                            </button>
                                                                        </h2>
                                                                        <div
                                                                            id={`collapse-${index}`}
                                                                            className="accordion-collapse collapse"
                                                                            aria-labelledby={`heading-${index}`}
                                                                        >
                                                                            <div className="accordion-body p-2 small" style={{ maxHeight: "150px", overflowY: "auto" }}>
                                                                                {mat.inasistencias.length === 0 && mat.tardanzas.length === 0 && mat.retiros.length === 0 ? (
                                                                                    <div className="text-muted text-center py-1">Sin inasistencias, tardanzas ni retiros.</div>
                                                                                ) : (
                                                                                    <ul className="list-group list-group-flush">
                                                                                        {mat.inasistencias.map((inas, iIdx) => (
                                                                                            <li key={`inas-${iIdx}`} className="list-group-item d-flex justify-content-between align-items-center py-1 px-1">
                                                                                                <span>{formatFechaLarga(inas.fecha)}</span>
                                                                                                <span className="badge bg-danger bg-opacity-75">Ausente</span>
                                                                                            </li>
                                                                                        ))}
                                                                                        {mat.tardanzas.map((tar, tIdx) => (
                                                                                            <li key={`tar-${tIdx}`} className="list-group-item d-flex justify-content-between align-items-center py-1 px-1">
                                                                                                <span>{formatFechaLarga(tar.fecha)}</span>
                                                                                                <span className="badge bg-warning bg-opacity-75 text-dark">Tarde</span>
                                                                                            </li>
                                                                                        ))}
                                                                                        {mat.retiros.map((ret, rIdx) => (
                                                                                            <li key={`ret-${rIdx}`} className="list-group-item d-flex justify-content-between align-items-center py-1 px-1">
                                                                                                <span>{formatFechaLarga(ret.fecha)}</span>
                                                                                                <span className="badge bg-warning text-dark border border-dark">Retiro</span>
                                                                                            </li>
                                                                                        ))}
                                                                                    </ul>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* SECCIÓN 3: DIFERENCIAS / DISCREPANCIAS ENTRE DÍAS */}
                    <div className="row mb-4">
                        <div className="col-12">
                            <div className="card shadow-sm border-0 rounded-3">
                                <div className="card-header bg-dark bg-gradient text-white fw-bold d-flex align-items-center justify-content-between py-2">
                                    <span>
                                        <i className="fa-solid fa-code-compare me-2"></i>
                                        Diferencias entre Asistencia General y Asistencia por Materia
                                    </span>
                                    <span className="badge bg-secondary">
                                        {discrepancias.length} Discrepancia(s)
                                    </span>
                                </div>
                                <div className="card-body">
                                    {discrepancias.length > 0 ? (
                                        <div className="table-responsive">
                                            <table className="table table-hover align-middle mb-0">
                                                <thead className="table-light small">
                                                    <tr>
                                                        <th>Fecha</th>
                                                        <th>Asistencia General</th>
                                                        <th>Materia</th>
                                                        <th>Asistencia Materia</th>
                                                        <th>Observación / Diferencia</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="small">
                                                    {discrepancias.map((disc, idx) => (
                                                        <tr key={idx}>
                                                            <td className="fw-semibold text-nowrap">{formatFechaLarga(disc.fecha)}</td>
                                                            <td>
                                                                <span className={`badge bg-${disc.estadoDiario === 'Presente' ? 'success' : disc.estadoDiario === 'Ausente' ? 'danger' : 'warning text-dark'}`}>
                                                                    {disc.estadoDiario}
                                                                </span>
                                                            </td>
                                                            <td className="fw-semibold">{disc.nombreMateria}</td>
                                                            <td>
                                                                <span className={`badge bg-${disc.estadoMateria === 'Presente' ? 'success bg-opacity-75' : disc.estadoMateria === 'Ausente' ? 'danger bg-opacity-75' : 'warning text-dark bg-opacity-75'}`}>
                                                                    {disc.estadoMateria}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                <span className={`text-${disc.tipoBadge} fw-semibold`}>
                                                                    <i className="fa-solid fa-triangle-exclamation me-1"></i>
                                                                    {disc.detalle}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    ) : (
                                        <div className="alert alert-success d-flex align-items-center mb-0" role="alert">
                                            <i className="fa-solid fa-circle-check fs-4 me-3"></i>
                                            <div>
                                                <strong>No se detectaron inconsistencias.</strong> Todos los registros de asistencia diaria concuerdan con las asistencias registradas por materias.
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
        </div>
    );
}

export default AsistenciaDetalleEstudiante;
